import { z } from 'zod';

export interface SortSpec {
  field: string;
  dir: 'asc' | 'desc';
}

export interface ListParams {
  page?: number;
  size?: number;
  /** Either structured sorts or a preformatted `field,asc;other,desc` string. */
  sort?: SortSpec[] | string;
  filter?: Record<string, string | number | boolean | null | undefined>;
}

/** Server page shape used by every list endpoint (`/api/v1/<context>/...?page=&size=&sort=&filter[x]=`). */
export interface Page<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages?: number;
}

/**
 * Serialises list parameters into the query convention used by server-driven grids:
 * `page`, `size`, `sort=field,asc` (multiple sorts joined by `;`) and `filter[field]=value`.
 * Null/undefined filter values are omitted.
 */
export function toListQuery(params: ListParams): Record<string, string> {
  const out: Record<string, string> = {};
  if (params.page !== undefined) out.page = String(params.page);
  if (params.size !== undefined) out.size = String(params.size);
  if (params.sort !== undefined) {
    const sort = typeof params.sort === 'string' ? params.sort : params.sort.map((s) => `${s.field},${s.dir}`).join(';');
    if (sort.length > 0) out.sort = sort;
  }
  if (params.filter) {
    for (const [field, value] of Object.entries(params.filter)) {
      if (value === undefined || value === null) continue;
      // `q` is free text and is a top-level parameter on the wire (`ListQuery.q`), not a filter. Screens
      // carry it in their filter bag because that is where their search box writes, but sending it as
      // `filter[q]` puts it in a map no specification reads — a search that quietly returns everything.
      if (field === 'q') out.q = String(value);
      else out[`filter[${field}]`] = String(value);
    }
  }
  return out;
}

/** Parses a `field,asc;other,desc` sort string back into specs (inverse of {@link toListQuery}). */
export function parseSort(sort: string | undefined): SortSpec[] {
  if (!sort) return [];
  const out: SortSpec[] = [];
  for (const part of sort.split(';')) {
    const [field, dir] = part.split(',');
    if (!field) continue;
    out.push({ field: field.trim(), dir: dir?.trim().toLowerCase() === 'desc' ? 'desc' : 'asc' });
  }
  return out;
}

/**
 * Zod schema for {@link Page}. Tolerates Spring-Data style bodies (`number` instead of `page`,
 * `numberOfElements`, nested `page` object) by normalising them first.
 */
export function pageSchema<T extends z.ZodType>(item: T): z.ZodType<Page<z.infer<T>>> {
  const shape = z.object({
    content: z.array(item),
    page: z.number().int().nonnegative(),
    size: z.number().int().nonnegative(),
    totalElements: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative().optional(),
  });
  const normalised = z.preprocess((raw) => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      return Array.isArray(raw) ? { content: raw, page: 0, size: raw.length, totalElements: raw.length, totalPages: 1 } : raw;
    }
    const r = raw as Record<string, unknown>;
    const nested = typeof r.page === 'object' && r.page !== null ? (r.page as Record<string, unknown>) : undefined;
    const content = Array.isArray(r.content) ? r.content : Array.isArray(r.items) ? r.items : r.content;
    const page = typeof r.page === 'number' ? r.page : (nested?.number ?? r.number ?? 0);
    const size = r.size ?? nested?.size ?? (Array.isArray(content) ? content.length : 0);
    const totalElements = r.totalElements ?? nested?.totalElements ?? r.total ?? (Array.isArray(content) ? content.length : 0);
    const totalPages = r.totalPages ?? nested?.totalPages;
    return { ...r, content, page, size, totalElements, totalPages };
  }, shape);
  return normalised as unknown as z.ZodType<Page<z.infer<T>>>;
}

/** Empty page used as placeholder data. */
export function emptyPage<T>(size = 25): Page<T> {
  return { content: [], page: 0, size, totalElements: 0, totalPages: 0 };
}
